/* The mobile bar delegates to the original submit button: one form, one
   variant source, and the theme's existing validation/cart drawer handler. */
if (!customElements.get('mam-mobile-atc')) {
  customElements.define('mam-mobile-atc', class extends HTMLElement {
    connectedCallback() {
      this.original = document.getElementById(this.dataset.buttonId);
      this.form = this.original?.form;
      if (!this.original || !this.form) return;
      this.button = this.querySelector('button');
      this.label = this.button.querySelector('span');
      this.price = this.querySelector('.mam-mobile-atc__price');
      this.media = window.matchMedia('(max-width: 749px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.button.addEventListener('click', () => {
        if (this.original.disabled || this.original.getAttribute('aria-disabled') === 'true') return;
        this.original.click();
      }, options);
      this.form.addEventListener('invalid', () => {
        this.form.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, { ...options, capture: true });
      window.addEventListener('resize', this.schedule, options);
      this.media.addEventListener('change', this.schedule, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      document.addEventListener('focusin', this.schedule, options);
      document.addEventListener('focusout', this.schedule, options);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.original, { subtree: true, childList: true, characterData: true, attributes: true });
      this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.overlays = [...document.querySelectorAll('cart-drawer, cart-notification, modal-dialog, product-modal, quick-add-modal, .header__offcanvas')];
      this.overlays.forEach(element => this.observer.observe(element, { attributes: true, attributeFilter: ['class', 'open'] }));
      this.error = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
      if (this.error) this.observer.observe(this.error, { attributes: true, childList: true, subtree: true });
      this.sizeObserver = new ResizeObserver(this.schedule);
      this.sizeObserver.observe(this);
      this.sizeObserver.observe(this.original);
      this.sync();
    }
    sync() {
      const sourcePrice = this.original.querySelector('.product-pricing');
      const priceHTML = sourcePrice && !sourcePrice.hidden ? sourcePrice.innerHTML : '';
      if (this.priceHTML !== priceHTML) {
        this.priceHTML = priceHTML;
        this.price.replaceChildren();
        if (priceHTML) {
          const copy = sourcePrice.cloneNode(true);
          copy.removeAttribute('id');
          this.price.append(copy);
        }
      }
      const label = this.original.querySelector('[data-mam-atc-label]')?.textContent.trim();
      if (label && this.label.textContent !== label) this.label.textContent = label;
      this.button.disabled = this.original.disabled || this.original.getAttribute('aria-disabled') === 'true';
      const loading = this.original.classList.contains('loading');
      this.button.classList.toggle('loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
      if (this.original.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', this.original.getAttribute('aria-haspopup'));
      const inputFocused = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
      const overlayOpen = document.body.classList.contains('overflow-hidden') ||
        this.overlays.some(element => element.classList.contains('active') || element.hasAttribute('open'));
      const errorVisible = this.error && !this.error.hidden;
      if (errorVisible && !this.errorWasVisible) {
        this.error.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      this.errorWasVisible = errorVisible;
      // Keep the mobile action stable through the entire page, including
      // when the original button, benefits, reviews or footer are visible.
      const visible = this.media.matches && !overlayOpen && !inputFocused;
      this.hidden = !visible;
      document.body.classList.toggle('mam-mobile-atc-visible', visible);
      if (visible) {
        const height = `${this.getBoundingClientRect().height}px`;
        if (document.body.style.getPropertyValue('--mam-mobile-atc-height') !== height) {
          document.body.style.setProperty('--mam-mobile-atc-height', height);
        }
      }
      // Reserve the bar's height for the entire mobile page, avoiding scroll jumps.
      document.body.classList.toggle('mam-mobile-atc-enabled', this.media.matches);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.sizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      document.body.classList.remove('mam-mobile-atc-enabled', 'mam-mobile-atc-visible');
      document.body.style.removeProperty('--mam-mobile-atc-height');
    }
  });
}
